import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Printer, Download } from 'lucide-react';
import api from '../services/api';

const ReportCardPage = () => {
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('child');
  const [child, setChild] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [completedWork, setCompletedWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);

  useEffect(() => {
    loadData();
  }, [childId]);

  const loadData = async () => {
    try {
      const [childrenData, subjectsData, workData, userData] = await Promise.all([
        api.getChildren(),
        api.getSubjects(),
        api.getCompletedWork(childId),
        api.getCurrentUser()
      ]);
      
      const childData = childrenData.find(c => c.id === childId);
      setChild({ ...childData, schoolName: userData.school_name || 'Homeschool' });
      setSubjects(subjectsData);
      setCompletedWork(workData);
      
      // Load semesters and set the first one as default
      if (userData.semesters && userData.semesters.length > 0) {
        const normalizedSemesters = userData.semesters.map(sem => ({
          name: sem.name,
          start_date: sem.start_date || sem.startDate,
          end_date: sem.end_date || sem.endDate
        }));
        setSemesters(normalizedSemesters);
        setSelectedSemester(normalizedSemesters[0]);
      } else {
        // Default semesters if none exist
        const defaultSemesters = [
          { name: 'Fall 2024', start_date: '2024-08-01', end_date: '2024-12-31' },
          { name: 'Spring 2025', start_date: '2025-01-01', end_date: '2025-05-31' }
        ];
        setSemesters(defaultSemesters);
        setSelectedSemester(defaultSemesters[0]);
      }
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  if (loading) {
    return <div className="p-8 text-center">Loading report card...</div>;
  }

  if (!child) {
    return <div className="p-8 text-center">Student not found</div>;
  }

  if (!selectedSemester) {
    return <div className="p-8 text-center">No semester data available. Please configure semesters in Settings.</div>;
  }

  // Filter completed work by selected semester date range
  const semesterWork = completedWork.filter(work => {
    // Use completed_date or submitted_date for filtering
    const workDate = work.completed_date || work.submitted_date;
    if (!workDate) return false;
    
    const date = new Date(workDate);
    const startDate = new Date(selectedSemester.start_date);
    const endDate = new Date(selectedSemester.end_date);
    
    return date >= startDate && date <= endDate;
  });

  // Group work by subject
  const workBySubject = semesterWork.reduce((acc, work) => {
    const subject = subjects.find(s => 
      String(s.id) === String(work.subject_id) || 
      (s.replaces_default_id && String(s.replaces_default_id) === String(work.subject_id))
    );
    
    if (subject) {
      const key = subject.id;
      if (!acc[key]) {
        acc[key] = { subject, work: [] };
      }
      acc[key].work.push(work);
    }
    return acc;
  }, {});

  // Calculate subject stats using weighted points
  const subjectStats = Object.values(workBySubject).map(({ subject, work }) => {
    const totalPoints = work.reduce((sum, w) => sum + (w.points_possible || 0), 0);
    const earnedPoints = work.reduce((sum, w) => sum + (w.points_awarded || 0), 0);
    const avgPercentage = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
    const letterGrade = getLetterGrade(avgPercentage);
    return {
      subject,
      avgPercentage: Math.round(avgPercentage),
      letterGrade,
      assignmentCount: work.length
    };
  });

  function getLetterGrade(percentage) {
    if (percentage >= 98) return 'A+';
    if (percentage >= 94) return 'A';
    if (percentage >= 93) return 'A-';
    if (percentage >= 90) return 'B+';
    if (percentage >= 87) return 'B';
    if (percentage >= 85) return 'B-';
    if (percentage >= 82) return 'C+';
    if (percentage >= 79) return 'C';
    if (percentage >= 77) return 'C-';
    if (percentage >= 74) return 'D+';
    if (percentage >= 71) return 'D';
    if (percentage >= 70) return 'D-';
    return 'F';
  }

  // Calculate attendance for the semester
  // Get all unique due dates from assignments
  const allDueDates = [...new Set(semesterWork.map(a => {
    const date = new Date(a.due_date);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  }))];
  
  // Count days as "present" if student submitted ANY assignment that was due on that day
  // (regardless of when they submitted it)
  const daysPresent = allDueDates.filter(dueDate => {
    // Check if any assignment due on this date was submitted
    return semesterWork.some(assignment => {
      const assignmentDueDate = new Date(assignment.due_date).toISOString().split('T')[0];
      const hasSubmission = assignment.submitted_date || assignment.completed_date;
      return assignmentDueDate === dueDate && hasSubmission;
    });
  }).length;
  
  const totalDays = allDueDates.length;
  const attendanceRate = totalDays > 0 ? Math.round((daysPresent / totalDays) * 100) : 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print/Download Buttons - Hidden when printing */}
      <div className="no-print sticky top-0 z-10 bg-white border-b p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Semester:</label>
          <select 
            value={selectedSemester.name}
            onChange={(e) => {
              const semester = semesters.find(s => s.name === e.target.value);
              setSelectedSemester(semester);
            }}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {semesters.map((sem, idx) => (
              <option key={idx} value={sem.name}>{sem.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-3">
          <Button onClick={handlePrint} variant="outline">
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
          <Button onClick={handleDownloadPDF} className="bg-blue-600">
            <Download className="w-4 h-4 mr-2" />
            Save as PDF
          </Button>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-4xl mx-auto bg-white print:mx-0 print:max-w-none print:pt-0" style={{ padding: '1in', paddingTop: '1in' }}>
        {/* Header */}
        <div className="text-center mb-8 border-b-4 border-blue-600 pb-6 print:border-b-2 print:border-slate-300">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Report Card</h1>
          <p className="text-lg text-slate-600">{child.schoolName}</p>
        </div>

        {/* Student Information */}
        <div className="mb-8 p-6 bg-blue-50 rounded-lg print:bg-transparent print:border print:border-slate-300">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Student Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-600">Student Name</p>
              <p className="text-lg font-semibold text-slate-900">{child.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Grade</p>
              <p className="text-lg font-semibold text-slate-900">{child.grade || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Semester</p>
              <p className="text-lg font-semibold text-slate-900">{selectedSemester.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Period</p>
              <p className="text-lg font-semibold text-slate-900">
                {new Date(selectedSemester.start_date).toLocaleDateString()} - {new Date(selectedSemester.end_date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Subject Grades */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Subject Performance</h2>
          {subjectStats.length > 0 ? (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-300 p-3 text-left">Subject</th>
                  <th className="border border-slate-300 p-3 text-center">Average</th>
                  <th className="border border-slate-300 p-3 text-center">Grade</th>
                  <th className="border border-slate-300 p-3 text-center">Assignments</th>
                </tr>
              </thead>
              <tbody>
                {subjectStats.map((stat, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="border border-slate-300 p-3">
                      <span className="font-medium">{stat.subject.name}</span>
                    </td>
                    <td className="border border-slate-300 p-3 text-center">{stat.avgPercentage}%</td>
                    <td className="border border-slate-300 p-3 text-center">
                      <span className="font-bold text-lg">{stat.letterGrade}</span>
                    </td>
                    <td className="border border-slate-300 p-3 text-center">{stat.assignmentCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="border border-slate-300 rounded-lg p-6 text-center text-slate-600">
              No completed assignments found for this semester.
            </div>
          )}
        </div>

        {/* Attendance Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Attendance</h2>
          <div className="border border-slate-300 rounded-lg p-6 bg-slate-50 print:bg-transparent">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-600">{daysPresent}</p>
                <p className="text-sm text-slate-600">Days Present</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{totalDays}</p>
                <p className="text-sm text-slate-600">Total Days</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{attendanceRate}%</p>
                <p className="text-sm text-slate-600">Attendance Rate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-slate-300">
          <p className="text-center text-sm text-slate-600">
            This report card reflects the student's academic performance for {selectedSemester.name}
          </p>
          <p className="text-center text-sm text-slate-500 mt-2">
            Generated on {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body::before,
          body::after {
            display: none !important;
          }
          header, nav, .header, [role="banner"] {
            display: none !important;
          }
          @page {
            margin: 1.25in 1in 1in 1in;
            size: letter;
          }
          .print\\:mx-0 {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
          .print\\:pt-0 {
            padding-top: 0 !important;
          }
          .print\\:bg-transparent {
            background: transparent !important;
          }
          .print\\:border {
            border: 1px solid #cbd5e1 !important;
          }
          .print\\:border-b-2 {
            border-bottom-width: 2px !important;
          }
          .print\\:border-slate-300 {
            border-color: #cbd5e1 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReportCardPage;
